import { FC, useMemo } from 'react';

import { Box } from '@bonusly/design-system';

import { defaultStyle, getStyleFromName } from '../../design-system/avatar/utils/style';

interface Bubble {
	x: number; // percentage, represents the center of the bubble
	y: number; // percentage, represents the center of the bubble
	size: number; // percentage of container dimension
}

function mulberry32(seed: number) {
	let s = seed;
	return () => {
		s = (s + 0x6d2b79f5) | 0;
		let t = Math.imul(s ^ (s >>> 15), 1 | s);
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}

function hashString(str: string): number {
	let h = 0;
	for (let i = 0; i < str.length; i++) {
		h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
	}
	return Math.abs(h);
}

function shuffled<T>(arr: T[], rand: () => number): T[] {
	const out = [...arr];
	for (let i = out.length - 1; i > 0; i--) {
		const j = Math.floor(rand() * (i + 1));
		[out[i], out[j]] = [out[j], out[i]];
	}
	return out;
}

// Two-pass bubble generation:
//
// Pass 1 — background fill: 6×6 grid, large bubbles (22–30% of container),
// tight jitter (±30% of cell width). At this grid density each bubble is ~1.5×
// the cell size so adjacent bubbles always overlap, guaranteeing full coverage.
//
// Pass 2 — accent layer: 7×7 grid, ~40% of cells randomly selected, smaller
// bubbles (8–14%). Rendered on top of pass 1, adding visual variety without
// disrupting the solid background.
//
// Both passes are shuffled before rendering so z-stacking order is random.
function generateBubbles(seed: string): Bubble[] {
	const rand = mulberry32(hashString(seed));

	// --- Pass 1: background fill ---
	const bgGrid = 6;
	const bgStep = 100 / bgGrid;
	const bgCells = shuffled(
		Array.from({ length: bgGrid * bgGrid }, (_, i) => ({ row: Math.floor(i / bgGrid), col: i % bgGrid })),
		rand
	);
	const bgBubbles: Bubble[] = bgCells.map(({ row, col }) => ({
		x: (col + 0.5) * bgStep + (rand() - 0.5) * bgStep * 0.6,
		y: (row + 0.5) * bgStep + (rand() - 0.5) * bgStep * 0.6,
		size: 22 + rand() * 8,
	}));

	// --- Pass 2: accent layer ---
	const fgGrid = 7;
	const fgStep = 100 / fgGrid;
	const fgCells = shuffled(
		Array.from({ length: fgGrid * fgGrid }, (_, i) => ({ row: Math.floor(i / fgGrid), col: i % fgGrid })).filter(() => rand() < 0.4),
		rand
	);
	const fgBubbles: Bubble[] = fgCells.map(({ row, col }) => ({
		x: (col + 0.5) * fgStep + (rand() - 0.5) * fgStep,
		y: (row + 0.5) * fgStep + (rand() - 0.5) * fgStep,
		size: 10 + rand() * 6,
	}));

	return [...bgBubbles, ...fgBubbles];
}

interface AvatarRecipient {
	id: string;
	src: string;
	name?: string;
}

interface AvatarBubbleWrapProps {
	name?: string;
	src?: string;
	recipients?: AvatarRecipient[];
	/** Seed string for deterministic bubble layout — use bonus ID in the feed, a stable key in the gallery */
	seed?: string;
	/** Width of the white border ring around each bubble. Use a smaller value for thumbnails. */
	borderWidth?: string;
}

export const AvatarBubbleWrap: FC<AvatarBubbleWrapProps> = ({ name, src, recipients, seed = 'default', borderWidth = '2px' }) => {
	const bubbles = useMemo(() => generateBubbles(seed), [seed]);

	const sortedRecipients = useMemo(() => {
		if (!recipients) return undefined;
		return [...recipients].sort((a, b) => a.id.localeCompare(b.id));
	}, [recipients]);

	return (
		<Box position="relative" w="100%" h="100%" bg="white" overflow="hidden">
			{bubbles.map((bubble, i) => {
				const avatarSrc = sortedRecipients ? sortedRecipients[i % sortedRecipients.length].src : src;
				const avatarName = sortedRecipients ? (sortedRecipients[i % sortedRecipients.length].name ?? '') : (name ?? '');
				const style = avatarName ? getStyleFromName(avatarName) : defaultStyle;
				const initials = avatarName
					.split(' ')
					.map((w) => w[0])
					.filter(Boolean)
					.slice(0, 2)
					.join('')
					.toUpperCase();

				return (
					<Box
						key={`${seed}-${i}`}
						position="absolute"
						left={`${bubble.x}%`}
						top={`${bubble.y}%`}
						transform="translate(-50%, -50%)"
						width={`${bubble.size}%`}
						aspectRatio="1"
						borderRadius="full"
						overflow="hidden"
						boxShadow={`0 0 0 ${borderWidth} white`}
						aria-hidden
						sx={{ containerType: 'inline-size' }}
					>
						{avatarSrc ? (
							<Box as="img" src={avatarSrc} alt="" w="100%" h="100%" objectFit="cover" display="block" />
						) : (
							<Box
								w="100%"
								h="100%"
								display="flex"
								alignItems="center"
								justifyContent="center"
								bg={style.bg}
								color={style.color}
								fontSize="35cqw"
								fontWeight="medium"
								lineHeight="1"
							>
								{initials}
							</Box>
						)}
					</Box>
				);
			})}
		</Box>
	);
};
